import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { BulkButtons } from "@/components/common/BulkUpload";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DataTable } from "@/components/common/DataTable";

const Sel = ({ value, onChange, options, label, testid }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider">{label}</Label>
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
      data-testid={testid}
    >
      <option value="">—</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.code} · {o.name}</option>)}
    </select>
  </div>
);

export default function Mappings() {
  const { t } = useLang();
  const canEdit = useAuth().can("mappings", "edit");
  const [maps, setMaps] = useState([]);
  const [refs, setRefs] = useState({ buyers: [], products: [], brands: [], packs: [], fgs: [], cartons: [], plastics: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const loadAll = async () => {
    const [m, b, p, br, pk, fg, mc, pl] = await Promise.all([
      api.get("/mappings"),
      api.get("/masters/buyers"),
      api.get("/masters/products"),
      api.get("/masters/brands"),
      api.get("/masters/packing-styles"),
      api.get("/masters/finish-goods"),
      api.get("/masters/cartons"),
      api.get("/masters/plastics"),
    ]);
    setMaps(m.data);
    setRefs({ buyers: b.data, products: p.data, brands: br.data, packs: pk.data, fgs: fg.data, cartons: mc.data, plastics: pl.data });
  };
  useEffect(() => { loadAll(); }, []);

  const dict = {};
  [...refs.buyers, ...refs.products, ...refs.brands, ...refs.packs, ...refs.fgs, ...refs.cartons, ...refs.plastics]
    .forEach((x) => { dict[x.id] = x; });

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm(m); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/mappings/${editing.id}`, form);
      else await api.post("/mappings", form);
      toast.success(t("save"));
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/mappings/${id}`);
    loadAll();
  };

  const ref = (id) => dict[id] || {};
  const comp = (idKey) => ({ key: idKey, value: (m) => `${ref(m[idKey]).code || "-"} · ${ref(m[idKey]).name || ""}`, className: "font-mono text-xs whitespace-nowrap" });
  const columns = [
    { key: "buyer_id", label: t("buyers"), value: (m) => ref(m.buyer_id).name },
    { key: "product_id", label: t("products"), value: (m) => ref(m.product_id).name },
    { key: "brand_id", label: t("brands"), value: (m) => ref(m.brand_id).name },
    { key: "packing_style_id", label: t("packing_styles"), value: (m) => ref(m.packing_style_id).name },
    { ...comp("fg_id"), label: t("finish_goods") },
    { ...comp("carton_id"), label: t("cartons") },
    { ...comp("plastic_id"), label: t("plastics") },
    { key: "actions", label: "", filter: false, sort: false, render: (m) => canEdit && (
      <div className="flex justify-end whitespace-nowrap">
        <Button variant="ghost" size="sm" onClick={() => openEdit(m)} data-testid={`mapping-edit-${m.id}`}><Pencil size={14} /></Button>
        <Button variant="ghost" size="sm" onClick={() => remove(m.id)} className="text-red-600" data-testid={`mapping-delete-${m.id}`}><Trash2 size={14} /></Button>
      </div>
    ) },
  ];

  return (
    <div className="space-y-6" data-testid="mappings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("mappings")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("mapping_selector")} → 1 FG + 1 Carton + 1 Plastic</p>
        </div>
        {canEdit && <div className="flex flex-wrap gap-2">
          <BulkButtons kind="mappings" title={t("mappings")} onDone={loadAll} />
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="mapping-add-btn">
            <Plus size={14} className="mr-2" />{t("add")}
          </Button>
        </div>}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <DataTable columns={columns} rows={maps} testid="mappings-table" />
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="mapping-form-dialog">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add")} - {t("mappings")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Sel value={form.buyer_id} onChange={(v) => setForm({ ...form, buyer_id: v })} options={refs.buyers} label={t("buyers")} testid="mapping-buyer-select" />
            <Sel value={form.product_id} onChange={(v) => setForm({ ...form, product_id: v })} options={refs.products} label={t("products")} testid="mapping-product-select" />
            <Sel value={form.brand_id} onChange={(v) => setForm({ ...form, brand_id: v })} options={refs.brands} label={t("brands")} testid="mapping-brand-select" />
            <Sel value={form.packing_style_id} onChange={(v) => setForm({ ...form, packing_style_id: v })} options={refs.packs} label={t("packing_styles")} testid="mapping-pack-select" />
            <Sel value={form.fg_id} onChange={(v) => setForm({ ...form, fg_id: v })} options={refs.fgs} label={t("finish_goods")} testid="mapping-fg-select" />
            <Sel value={form.carton_id} onChange={(v) => setForm({ ...form, carton_id: v })} options={refs.cartons} label={t("cartons")} testid="mapping-carton-select" />
            <Sel value={form.plastic_id} onChange={(v) => setForm({ ...form, plastic_id: v })} options={refs.plastics} label={t("plastics")} testid="mapping-plastic-select" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="mapping-cancel-btn">{t("cancel")}</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="mapping-save-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
